import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { css } from "styled-system/css";
import Markdown from "react-markdown";
import { RefreshCw } from "lucide-react";
import { api } from "~/lib/api";
import { ed, btn } from "~/components/editorial";

interface BriefingResponse {
  date: string;
  content: string;
  createdAt: string;
  isToday: boolean;
}

const dateFmt: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
};

/** The morning briefing, set as a column of serif prose. */
export function DailyBriefing({ label }: { label: string }) {
  const queryClient = useQueryClient();

  const {
    data: briefingData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["daily-briefing"],
    queryFn: () =>
      api.get<{ data: BriefingResponse | null }>("/briefing/today"),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { content: string } }>("/briefing/generate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["daily-briefing"] });
    },
  });

  const briefing = briefingData?.data ?? null;
  const busy = generateMutation.isPending || isFetching;

  const todayFormatted = new Date().toLocaleDateString("en-US", dateFmt);
  const briefingDateFormatted = briefing
    ? new Date(briefing.date).toLocaleDateString("en-US", dateFmt)
    : null;

  return (
    <section>
      <div className={`${ed.sect} ${ed.sectRow}`}>
        <span>{label}</span>
        {briefing && (
          <button
            onClick={() => generateMutation.mutate()}
            disabled={busy}
            className={btn.sentenceOutline}
            title="Regenerate briefing"
          >
            <RefreshCw size={12} className={busy ? styles.spinning : ""} />
            Regenerate
          </button>
        )}
      </div>
      <h2 className={ed.h2}>The briefing for {todayFormatted}</h2>

      {isLoading ? (
        <div className={ed.loadingSm}>Fetching the briefing…</div>
      ) : briefing ? (
        <>
          {!briefing.isToday && briefingDateFormatted && (
            <p className={ed.method}>
              From {briefingDateFormatted}. Today's briefing is written on the
              next worker run.
            </p>
          )}
          <div className={styles.markdown}>
            <Markdown>{briefing.content}</Markdown>
          </div>
          <p className={ed.method}>
            Drafted by a language model from recent GitHub and JIRA activity,
            on its own rolling window. Check figures against the ledger above.
          </p>
        </>
      ) : (
        <div className={styles.empty}>
          <p className={ed.prose}>No briefing has been written yet.</p>
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className={btn.sentence}
          >
            {generateMutation.isPending ? (
              <>
                <RefreshCw size={12} className={styles.spinning} />
                Writing…
              </>
            ) : (
              "Write the briefing"
            )}
          </button>
          {generateMutation.isError && (
            <p className={styles.error}>
              The briefing couldn't be written. Check that ANTHROPIC_API_KEY is
              configured.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

const styles = {
  spinning: css({ animation: "spin 1s linear infinite" }),
  markdown: css({
    fontFamily: "serif",
    fontSize: "16.5px",
    lineHeight: 1.6,
    color: "ed.ink",
    maxWidth: "700px",
    "& p": { m: 0, mb: "12px", _last: { mb: 0 } },
    "& strong": { fontWeight: 700 },
    "& code": { fontFamily: "mono", fontSize: "0.9em" },
    "& ul, & ol": { m: 0, mb: "12px", pl: "20px" },
    "& ul": { listStyleType: "disc" },
    "& ol": { listStyleType: "decimal" },
    "& li": { mb: "4px" },
    "& li::marker": { color: "ed.ink3" },
    "& h1, & h2, & h3": {
      fontFamily: "sans",
      fontWeight: 700,
      fontSize: "12px",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "ed.ink2",
      mt: "18px",
      mb: "6px",
      _first: { mt: 0 },
    },
  }),
  empty: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
  }),
  error: css({
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink",
    bg: "ed.cautionTint",
    px: "6px",
    py: "2px",
    m: 0,
  }),
};
